import { PagePaginationRequest, PagePaginationResponse } from "@/api/shared";

export enum TransactionProcessor {
  NMI = "nmi",
  UG = "ug",
  APPLE = "apple",
  GOOGLE = "google",
}

export enum TransactionStatus {
  COMPLETED = "completed",
  FAILED = "failed",
}

export enum PaymentEventStatus {
  STARTED = "started",
  PENDING = "pending",
  COMPLETED = "completed",
  FAILED = "failed",
}

export enum PaymentEventStage {
  CHECKOUT_INIT = "checkout_init",
  PRODUCT_UPDATED = "product_updated",
  ENROLLMENT_CHECK = "enrollment_check",
  ACS_CHALLENGE = "acs_challenge",
  THREE_D_SALE = "three_d_sale",
  DIRECT_SALE = "direct_sale",
  RECURRING_CHARGE = "recurring_charge",
  WEBHOOK_CONFIRM = "webhook_confirm",
}

/**
 * Mirrors backend OrderStatus. No `ABANDONED` here — stale pending Orders
 * are derived as abandoned in the UI from age + type, not stored.
 */
export enum OrderStatus {
  PENDING = "pending",
  PAID = "paid",
  CANCELLED = "cancelled",
  FAILED = "failed",
  REVOKED = "revoked",
}

export enum OrderType {
  RECURRING = "recurring",
  GENERAL = "general",
  SPEND_PURCHASE = "spend_purchase",
}

export type PaymentRow = {
  /** Same as orderId. Required by react-admin's RaRecord shape. */
  id: string;
  orderId: string;
  createdAt: string;
  status: OrderStatus;
  type: OrderType;
  price: number;
  productTitle?: string;
  /** True if the order's product is a recurring (non-one-time) plan. */
  isRecurringProduct: boolean;
  user?: {
    id: string;
    email?: string;
    firstName?: string;
    lastName?: string;
  };
  processor?: TransactionProcessor;
  latestActivityAt?: string;
  latestStage?: PaymentEventStage;
  latestEventStatus?: PaymentEventStatus;
  latestErrorCode?: string;
  latestErrorMessage?: string;
  providerTransactionId?: string;
};

export type ProductItemSummary = {
  name: string;
  isOneTime: boolean;
  durationDays?: number;
  durationMonths?: number;
};

export type ProductSummary = {
  id: string;
  name: string;
  quantity: number;
  items: ProductItemSummary[];
};

export type PaymentTransactionSummary = {
  id: string;
  status: TransactionStatus;
  processor: TransactionProcessor;
  actualAmount: number;
  actualCurrency?: string;
  providerTransactionId?: string;
  errorCode?: string;
  errorMessage?: string;
  createdAt: string;
};

export type PaymentEventRow = {
  id: string;
  stage: PaymentEventStage;
  status: PaymentEventStatus;
  createdAt: string;
  providerTransactionId?: string;
  errorCode?: string;
  errorMessage?: string;
  payload: unknown;
};

export type PaymentAttempt = {
  attemptId: string;
  events: PaymentEventRow[];
};

export type PaymentDetail = {
  orderId: string;
  status: OrderStatus;
  type: OrderType;
  price: number;
  createdAt: string;
  paidAt?: string;
  /**
   * Raw product structure. FE derives the "Subscription" / "One-time" chip
   * label, product title, duration display, etc. List view stays on
   * aggregation-derived fields (isRecurringProduct, productTitle on PaymentRow)
   * for paginated performance; detail view sends raw data so new product
   * fields don't require a BE round-trip.
   */
  products: ProductSummary[];
  user?: {
    id: string;
    email?: string;
    firstName?: string;
    lastName?: string;
  };
  /**
   * All canonical Transaction rows for this Order, newest first. Multiple
   * rows reveal failed-then-retried attempts AND accidental duplicate
   * successful charges (instead of hiding them behind a single "latest"
   * pick) so admins can spot data-integrity issues.
   */
  transactions: PaymentTransactionSummary[];
  attempts: PaymentAttempt[];
};

export type ListPaymentsRequest = PagePaginationRequest<{
  statuses?: OrderStatus[];
  types?: OrderType[];
  processors?: TransactionProcessor[];
  userId?: string;
  orderId?: string;
  fromDate?: string;
  toDate?: string;
  search?: string;
  sortBy?: string;
  sortDirection?: "asc" | "desc";
}>;

export type ListPaymentsResponse = PagePaginationResponse<PaymentRow>;

export type GetPaymentDetailRequest = { orderId: string };
export type GetPaymentDetailResponse = PaymentDetail;
